"use client";

import { Fragment } from "react";
import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import {
  type CrossResult,
  type Gamete,
  gameteParts,
  type LabelPart,
  reduceFraction,
} from "@/lib/tool-kit/biology-genetics/punnett";

/** Box tints, one per phenotype (cycling); the phenotype table repeats the swatch. */
export const PHENOTYPE_TINTS = [
  "bg-amber-100/70 dark:bg-amber-400/20",
  "bg-sky-100/70 dark:bg-sky-400/20",
  "bg-rose-100/70 dark:bg-rose-400/20",
  "bg-emerald-100/70 dark:bg-emerald-400/20",
  "bg-violet-100/70 dark:bg-violet-400/20",
  "bg-orange-100/70 dark:bg-orange-400/20",
] as const;

export function tintFor(index: number): string {
  return PHENOTYPE_TINTS[index % PHENOTYPE_TINTS.length];
}

/** Letters with their superscripts: A, a, X^A, Y. */
export function PartsLabel({ parts }: { parts: readonly LabelPart[] }) {
  return (
    <>
      {parts.map((part, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: label parts are positional
        <Fragment key={index}>
          {part.base}
          {part.sup ? <sup>{part.sup}</sup> : null}
        </Fragment>
      ))}
    </>
  );
}

/** Several genes' labels side by side, separated by a thin space. */
export function GeneLabels({
  genes,
}: {
  genes: readonly (readonly LabelPart[])[];
}) {
  return (
    <span className="whitespace-nowrap font-mono">
      {genes.map((parts, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: one label per gene, in order
        <Fragment key={index}>
          {index > 0 ? " " : null}
          <PartsLabel parts={parts} />
        </Fragment>
      ))}
    </span>
  );
}

export function GameteLabel({ gamete }: { gamete: Gamete }) {
  return <GeneLabels genes={gameteParts(gamete)} />;
}

export function SquareTable({
  result,
  caption,
  cornerLabel,
  cellLabel,
}: {
  result: CrossResult;
  caption: string;
  cornerLabel: string;
  /** Accessible name for a box: "Offspring AA, dominant (A)". */
  cellLabel: (genotypeIndex: number, phenotypeIndex: number) => string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th className={TH} scope="col">
              <span className="sr-only">{cornerLabel}</span>
            </th>
            {result.cols.map((gamete) => (
              <th
                className={`${TH} text-center normal-case tracking-normal`}
                key={gamete.key}
                scope="col"
              >
                <GameteLabel gamete={gamete} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {result.rows.map((row, rowIndex) => (
            <tr key={row.key}>
              <th
                className={`${TH} text-center normal-case tracking-normal`}
                scope="row"
              >
                <GameteLabel gamete={row} />
              </th>
              {result.cells[rowIndex].map((cell, colIndex) => (
                <td
                  aria-label={cellLabel(cell.genotype, cell.phenotype)}
                  className={`${TD} text-center ${tintFor(cell.phenotype)}`}
                  key={result.cols[colIndex].key}
                >
                  <GeneLabels genes={result.genotypes[cell.genotype].genes} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface CategoryRow {
  key: string;
  label: React.ReactNode;
  secondary?: React.ReactNode;
  count: number;
  tint?: string;
}

export function CategoryTable({
  head,
  rows,
  total,
  fmt,
}: {
  /** Label column, optional second column (or empty string), boxes, fraction, probability. */
  head: readonly [string, string, string, string, string];
  rows: readonly CategoryRow[];
  total: number;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
}) {
  const hasSecondary = rows.some((row) => row.secondary !== undefined);
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {head[0]}
            </th>
            {hasSecondary ? (
              <th className={TH} scope="col">
                {head[1]}
              </th>
            ) : null}
            <th className={`${TH} text-right`} scope="col">
              {head[2]}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {head[3]}
            </th>
            <th className={`${TH} text-right`} scope="col">
              {head[4]}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const fraction = reduceFraction(row.count, total);
            return (
              <tr key={row.key}>
                <td className={`${TD} ${row.tint ?? ""}`}>{row.label}</td>
                {hasSecondary ? <td className={TD}>{row.secondary}</td> : null}
                <td className={`${TD} text-right`}>{fmt(row.count)}</td>
                <td className={`${TD} text-right`}>
                  {fmt(fraction.numerator)}/{fmt(fraction.denominator)}
                </td>
                <td className={`${TD} text-right`}>
                  {fmt((row.count / total) * 100, {
                    maximumFractionDigits: 2,
                  })}
                  %
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
